import { getEntityScope } from './entity-scope-read';
import type { EntityScopeState } from '../slice/entityScopeSlice';

type ContextDropdownOption = {
  value: string;
  label: string;
};

/**
 * Values that mean "no context", including the two string sentinels older
 * builds persisted. `callBack.service` used to write
 * `` `${parsedTokenData.CurrentContext}` ``, so an absent claim became the
 * literal "undefined" — and this function's `if (!raw)` guard, written for
 * null, let it through (DV-3012 / D12).
 *
 * The write is fixed too, but this MUST stay: every already-logged-in browser
 * holds the sentinel until that user's next login, so this is the migration.
 */
const ABSENT = new Set(['', 'undefined', 'null']);

const parseCurrentContext = (raw: string | null): string => {
  if (!raw || ABSENT.has(raw)) return '';
  if (raw.includes('Internal')) {
    return raw.split('Internal')[0];
  }
  return raw.split('Customer')[0];
};

export const getCurrentContext = (): string => {
  // DV-3573 review finding 1: the SLICE first. `currentContext` is shared
  // across tabs and is re-derived from the token at login, so neither property
  // survives per-tab, token-stable switching. The slice is the one source that
  // names the account THIS tab is looking at — which is what every caller
  // means, and what a team invite must be scoped to.
  const scoped = getEntityScope();
  if (scoped.status === 'scoped' && scoped.entityName) return scoped.entityName;
  return parseCurrentContext(localStorage.getItem('currentContext'));
};

/**
 * The caller's role in the account THIS tab is looking at.
 *
 * <p>DV-3573 review finding 1. `localStorage.role` is shared across tabs and
 * was re-derived from the token on every refresh, so after token-stable
 * switching it names the login account's role, not this one's. The slice
 * stores the realm role, so it is returned as is.
 *
 * <p>Falls back to `localStorage` for a session persisted before the slice
 * carried a role, and for an unscoped tab. A switch into an account whose role
 * maps to nothing stores null on the slice AND removes `localStorage.role`,
 * so the fallback cannot resurrect the previous account's role there.
 */
export const getScopeRole = (): string | null => scopeRoleOf(getEntityScope());

/**
 * `getScopeRole()`'s rule for a scope already in hand, so `useScopeRole()`
 * (which subscribes to the slice) answers exactly as this module does.
 */
export const scopeRoleOf = (scope: Pick<EntityScopeState, 'status' | 'role'>): string | null => {
  if (scope.status === 'scoped' && scope.role) return scope.role;
  return localStorage.getItem('role');
};

/**
 * Whether a context was STORED — a different question from "does it parse to a
 * display name", which is what `getCurrentContext()` answers.
 *
 * `routeIncompleteRegistration` (useScopeBootstrap.ts) needs the first, and was
 * asking the second: an entity whose name is exactly a role suffix parses to
 * '' and would have been read as "this user never finished registering". Since
 * spec §3.2 demotes `currentContext` to display-only, a ROUTING decision has no
 * business reading it through a display parser at all (spec §4, close-out §5).
 *
 * Shares the ABSENT set, which is the migration for the legacy literal
 * "undefined" every browser still holds until its user's next login.
 */
export const hasStoredContext = (): boolean => {
  const raw = localStorage.getItem('currentContext');
  return !!raw && !ABSENT.has(raw);
};

export const getEntitySubEntitiesDropdown = (): ContextDropdownOption[] => {
  const currentContext = getCurrentContext();
  if (!currentContext) return [];
  return [
    {
      value: currentContext,
      label: currentContext
    }
  ];
};
